import { useCallback, useEffect, useState } from "react";
import { Megaphone, Check, ChevronLeft, ChevronRight, X } from "lucide-react";
// Quill 2 marks bullets as <ol><li data-list="bullet"> rather than <ul>, so
// without its stylesheet every bullet list renders as a numbered one. Imported
// here rather than relied on from the editor: the popup ships to users who may
// never load the admin page, and a later code-split would break it silently.
import "react-quill-new/dist/quill.snow.css";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { RELEASES_PENDING_URL, RELEASES_ACKNOWLEDGE_URL } from "../../constants/api";

/**
 * Pending release notes, shown once per session after sign-in.
 *
 * Dismissible by design: nothing here blocks the app, and anything left
 * unacknowledged comes back on the next login. The session flag below is what
 * stops it reappearing on every route change within one session — without it
 * the modal would remount and refetch each time AppLayout does.
 */
const SEEN_THIS_SESSION = "releases_popup_shown";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

const pad = (n) => String(n).padStart(2, "0");

export default function ReleaseAckModal() {
  const { fetch, create } = useApiMethods();

  const [releases, setReleases] = useState([]);
  const [index, setIndex]       = useState(0);
  const [open, setOpen]         = useState(false);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_THIS_SESSION)) return;
    if (!sessionStorage.getItem("token")) return;

    let cancelled = false;
    (async () => {
      const res = await fetch(RELEASES_PENDING_URL);
      if (cancelled) return;
      sessionStorage.setItem(SEEN_THIS_SESSION, "1");
      if (res?.status === "success" && (res.data || []).length) {
        setReleases(res.data);
        setIndex(0);
        setOpen(true);
      }
    })();
    return () => { cancelled = true; };
    // Runs once per mount. `fetch` is recreated every render by useApiMethods,
    // so listing it here would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = useCallback(() => setOpen(false), []);

  // Escape closes it. A dismissible dialog that traps the keyboard is worse
  // than no dialog — and Later is the same action, one key away.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const acknowledge = async (all) => {
    const current = releases[index];
    const payload = all ? { acknowledge_all: true } : { release_ids: [current.id] };

    const res = await create(RELEASES_ACKNOWLEDGE_URL, payload, false, setSaving);
    if (res?.status !== "success") {
      ToastMsg(res?.message || "Could not record that — please try again", "error");
      return;
    }

    if (all) {
      ToastMsg("All updates acknowledged", "success");
      setReleases([]);
      close();
      return;
    }

    const remaining = releases.filter((r) => r.id !== current.id);
    setReleases(remaining);
    if (!remaining.length) {
      ToastMsg("Update acknowledged", "success");
      close();
    } else {
      setIndex((i) => Math.min(i, remaining.length - 1));
    }
  };

  if (!open || !releases.length) return null;

  const release = releases[index];
  const many    = releases.length > 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4
                 bg-gray-950/40 backdrop-blur-[2px]
                 animate-in fade-in-0 duration-200"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="relative flex max-h-[86vh] w-full max-w-[540px] flex-col overflow-hidden
                   rounded-[14px] bg-white dark:bg-[#111113]
                   ring-1 ring-gray-900/[0.08] dark:ring-white/[0.08]
                   shadow-[0_1px_2px_rgba(0,0,0,0.04),0_12px_40px_-8px_rgba(0,0,0,0.18)]
                   animate-in zoom-in-[0.98] slide-in-from-bottom-2 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="release-title"
      >
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="px-6 pt-6 pb-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-md
                               bg-gray-100 dark:bg-white/[0.06]
                               ring-1 ring-inset ring-gray-900/[0.06] dark:ring-white/[0.06]">
                <Megaphone className="h-3.5 w-3.5 text-gray-600 dark:text-gray-300" />
              </span>
              <span className="text-[12px] font-medium text-gray-500 dark:text-gray-400">
                What&apos;s new
              </span>
              {many && (
                <span className="text-[12px] tabular-nums text-gray-300 dark:text-gray-600">
                  {pad(index + 1)} / {pad(releases.length)}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={close}
              title="Remind me at next login"
              aria-label="Close"
              className="-mr-1 rounded-md p-1.5 text-gray-400 transition
                         hover:bg-gray-100 hover:text-gray-600
                         dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <h2 id="release-title"
              className="text-[19px] font-semibold leading-snug tracking-[-0.011em]
                         text-gray-900 dark:text-gray-50">
            {release.title}
          </h2>

          <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12.5px] text-gray-500 dark:text-gray-400">
            {release.version && (
              <span className="rounded-[5px] border border-gray-200 px-1.5 py-px font-mono text-[11.5px]
                               text-gray-600 dark:border-white/10 dark:text-gray-300">
                {release.version}
              </span>
            )}
            {release.version && release.released_on && (
              <span className="text-gray-300 dark:text-gray-700">·</span>
            )}
            {release.released_on && <span>{fmtDate(release.released_on)}</span>}
          </div>
        </div>

        <div className="h-px bg-gray-900/[0.06] dark:bg-white/[0.06]" />

        {/* ── Body ─────────────────────────────────────────────────────── */}
        {/* The HTML here is sanitized server-side on write — see
            platform_core/releases/sanitize.py — so what reaches the browser has
            already been reduced to an allowlist of formatting tags. Anything
            saved before the editor landed is plain text with newlines, and is
            rendered as such rather than collapsing into one paragraph. */}
        <div className="flex-1 overflow-y-auto px-6 py-5 text-[13.5px] text-gray-600 dark:text-gray-300">
          {!release.body ? (
            <p className="text-gray-400 dark:text-gray-500">
              No further details were included with this update.
            </p>
          ) : release.body.includes("<") ? (
            <div className="release-body ql-editor"
                 dangerouslySetInnerHTML={{ __html: release.body }} />
          ) : (
            <p className="whitespace-pre-line">{release.body}</p>
          )}
        </div>

        <div className="h-px bg-gray-900/[0.06] dark:bg-white/[0.06]" />

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-3.5">
          {many && (
            <div className="mr-auto flex items-center gap-1">
              {/* Arrows sit next to the counter above rather than duplicating
                  it — the dots were a second, redundant position indicator. */}
              <button
                type="button"
                disabled={index === 0}
                onClick={() => setIndex((i) => i - 1)}
                aria-label="Previous update"
                className="rounded-md p-1.5 text-gray-400 transition
                           hover:bg-gray-100 hover:text-gray-700
                           disabled:pointer-events-none disabled:opacity-25
                           dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={index >= releases.length - 1}
                onClick={() => setIndex((i) => i + 1)}
                aria-label="Next update"
                className="rounded-md p-1.5 text-gray-400 transition
                           hover:bg-gray-100 hover:text-gray-700
                           disabled:pointer-events-none disabled:opacity-25
                           dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={close}
            disabled={saving}
            className={`rounded-md px-2.5 py-1.5 text-[13px] font-medium text-gray-500 transition
                        hover:text-gray-800 disabled:opacity-50
                        dark:hover:text-gray-200 ${many ? "" : "mr-auto"}`}
          >
            Later
          </button>

          {/* With one release "Got it" is the only affirmative action, so it
              takes the solid treatment. With several it steps down to the
              outline, because acknowledging everything is the likelier intent. */}
          <button
            type="button"
            onClick={() => acknowledge(false)}
            disabled={saving}
            className={
              many
                ? `inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5
                   text-[13px] font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50
                   dark:border-white/10 dark:bg-transparent dark:text-gray-200 dark:hover:bg-white/[0.06]`
                : `inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5
                   text-[13px] font-medium text-white transition hover:bg-gray-800
                   active:scale-[0.985] disabled:opacity-50
                   dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100`
            }
          >
            <Check className="h-3.5 w-3.5" /> Got it
          </button>

          {many && (
            <button
              type="button"
              onClick={() => acknowledge(true)}
              disabled={saving}
              className="rounded-md bg-gray-900 px-3 py-1.5 text-[13px] font-medium text-white
                         transition hover:bg-gray-800 active:scale-[0.985] disabled:opacity-50
                         dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              Acknowledge all {releases.length}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
